import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, Crown } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { isPromoDismissed, dismissPromo } from '../utils/conversionTracking';
import { a11yLabel } from '../utils/a11yLabels';

/**
 * Soft Promotional Banner - Founding Member positioning
 * Non-aggressive, professional tone
 */
const PromoBanner = ({ className = '' }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show banner if not dismissed
    if (!isPromoDismissed()) {
      // Small delay for better UX
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    dismissPromo();
    setVisible(false);
  };

  const handleCTA = () => {
    navigate('/membership');
  };

  if (!visible) return null;

  const content = {
    en: {
      badge: 'Founding Member',
      title: 'Start your sleep learning journey today',
      subtitle: 'Memberships from $2/month · AI learning tools with Aprendizaje IA Pro',
      cta: 'Get Started',
    },
    es: {
      badge: 'Miembro Fundador',
      title: 'Comienza tu viaje de aprendizaje del sueño hoy',
      subtitle: 'Membresías desde $2/mes · herramientas de IA con Aprendizaje IA Pro',
      cta: 'Comenzar',
    },
  };

  const t = content[language] || content.en;

  return (
    <div 
      className={`bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 px-4 ${className}`}
      data-testid="promo-banner"
    >
      <div className="container mx-auto flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="hidden sm:flex items-center gap-1 bg-white/20 px-2 py-1 rounded-full text-xs font-medium">
            <Crown className="h-3 w-3" />
            {t.badge}
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium truncate">{t.title}</span>
            <span className="hidden md:inline text-emerald-100">—</span>
            <span className="hidden md:inline text-emerald-100">{t.subtitle}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleCTA}
            className="bg-white text-emerald-700 hover:bg-emerald-50 font-medium"
            data-testid="promo-cta-btn"
          >
            {t.cta}
          </Button>
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-white/20 rounded-full transition-colors"
            aria-label={a11yLabel("Dismiss")}
            data-testid="promo-dismiss-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromoBanner;
